import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ConsoleRole, StaffIdentity } from '@fitting-room/contracts'

/**
 * What the console is holding, and how it got it.
 *
 * There are two credentials now and exactly one thing every call site cares
 * about: the string that goes in `Authorization: Bearer`. That is the whole
 * reason the migration can be gradual — a signed-in session and the shared
 * token are both bearers, so `adminQueries` and `api.ts` did not change by a
 * line when sign-in arrived.
 *
 * The two are kept under SEPARATE localStorage keys rather than one, and that
 * is deliberate:
 *
 *  - `fitting-room.staff-token` is the shared token, and its key must not move.
 *    It is typed into shop-floor tablets and it is what `e2e/` seeds. Renaming
 *    it would sign out every tablet in every boutique on deploy morning, which
 *    is the exact failure this whole migration is arranged to avoid.
 *  - `fitting-room.staff-session` is a signed-in person. It expires, it carries
 *    a role, and signing out must clear it WITHOUT clearing a shared token that
 *    somebody may still be relying on.
 *
 * The session wins when both are present: a person who has signed in has said
 * who they are, and answering as the anonymous shop token instead would show
 * them owner buttons they may not be entitled to.
 */

const TOKEN_KEY = 'fitting-room.staff-token'
const SESSION_KEY = 'fitting-room.staff-session'
const EVENT = 'fitting-room:staff-token'

/** What is written under `SESSION_KEY`. `expiresAt` is checked before it is used. */
export interface StoredSession {
  readonly token: string
  readonly expiresAt: string
  readonly identity: StaffIdentity
}

export type CredentialSource = 'shared-token' | 'google'

function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeRaw(key: string, value: string | null): void {
  try {
    if (value) window.localStorage.setItem(key, value)
    else window.localStorage.removeItem(key)
  } catch {
    /* private mode: the console simply asks again */
  }
  window.dispatchEvent(new Event(EVENT))
}

/**
 * The stored session, or nothing — as a pure function of what was stored and
 * what time it is.
 *
 * Pure, and exported, because it is the half of this module that can be wrong
 * in a way nobody notices: an expired session that is treated as valid draws a
 * console whose every request 401s, and a corrupt one that is not caught throws
 * inside a `useState` initialiser and takes the whole app down to a blank
 * screen. Neither is visible in a browser until it happens. The suite runs in
 * `node` with no DOM, so the DOM stays in the hook and the decisions come out
 * here where they can be tested.
 *
 * An expired session is dropped here rather than sent and refused. The server
 * would refuse it correctly — `verify` checks `exp` — but a console that has to
 * make a round trip to discover it is signed out spends that round trip drawing
 * a page it is about to replace.
 */
export function parseStoredSession(raw: string | null, now: number = Date.now()): StoredSession | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredSession
    if (!parsed?.token || !parsed?.identity?.role) return null
    /*
     * `Number.isFinite` first, and it is not defensive noise: `Date.parse` of
     * anything it cannot read is `NaN`, and `NaN <= now` is FALSE. Written as
     * the comparison alone, a session whose `expiresAt` is missing or corrupt
     * is not rejected — it is accepted as one that never expires. The one
     * stored value that should be trusted least would have become the only
     * immortal credential in the product.
     */
    const expiry = Date.parse(parsed.expiresAt)
    if (!Number.isFinite(expiry) || expiry <= now) return null
    return parsed
  } catch {
    // Written by an older build, or truncated by a full disk. Either way it is
    // not a credential; `null` means the gate is drawn instead of a crash.
    return null
  }
}

/**
 * Which of the two credentials answers, and what the console may draw from it.
 *
 * The session wins when both are present. A person who has signed in has said
 * who they are, and answering as the anonymous shop token instead would show a
 * stylist the owner-only buttons — which the endpoint would then refuse,
 * leaving her looking at a control that does not work.
 */
export function resolveCredential(
  token: string | null,
  session: StoredSession | null,
): Pick<StaffSessionState, 'token' | 'source' | 'identity' | 'role'> {
  if (session) {
    return { token: session.token, source: 'google', identity: session.identity, role: session.identity.role }
  }
  if (token) {
    // The shared token belongs to no one, so there is no identity to draw — the
    // console must not invent a name for it. `owner` because that is what the
    // server resolves it to; see `EnvStaffAuthProvider`.
    return { token, source: 'shared-token', identity: null, role: 'owner' }
  }
  return { token: null, source: null, identity: null, role: null }
}

function readSession(): StoredSession | null {
  const raw = readRaw(SESSION_KEY)
  const parsed = parseStoredSession(raw)
  // Clearing an unusable value is a side effect and stays out of the pure
  // function; doing it here means a corrupt entry is repaired once rather than
  // re-parsed on every render.
  if (raw && !parsed) writeRaw(SESSION_KEY, null)
  return parsed
}

export interface StaffSessionState {
  /** The bearer to send, whichever kind it is. `null` means signed out. */
  token: string | null
  source: CredentialSource | null
  /**
   * Who is signed in. `null` for the shared token, which belongs to no one —
   * and the console must not invent a name for it.
   */
  identity: StaffIdentity | null
  /**
   * What this credential may do, as far as the client knows.
   *
   * `owner` for the shared token because that is what the server resolves it
   * to. Client-side role is for DRAWING only: it decides whether a button is
   * shown, never whether an action is allowed. The endpoint decides that, and
   * `console-identity.test.ts` is where it is proved — a hidden button is a
   * courtesy, not a permission.
   */
  role: ConsoleRole | null
  /** Adopt a shared token typed into the gate. */
  setToken: (value: string) => void
  /** Store a session minted by `POST /api/auth/google`. */
  setSession: (session: StoredSession) => void
  /** Drop whichever credential is in use — and only that one. */
  clearToken: () => void
}

export function useStaffToken(): StaffSessionState {
  const [token, setTokenState] = useState<string | null>(() => readRaw(TOKEN_KEY))
  const [session, setSessionState] = useState<StoredSession | null>(() => readSession())

  useEffect(() => {
    const sync = () => {
      setTokenState(readRaw(TOKEN_KEY))
      setSessionState(readSession())
    }
    window.addEventListener(EVENT, sync)
    // `storage` fires in the OTHER tabs. Signing out in one must not leave a
    // second tab holding a console it can no longer talk to.
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const setToken = useCallback((value: string) => writeRaw(TOKEN_KEY, value.trim() || null), [])

  const setSession = useCallback((value: StoredSession) => {
    writeRaw(SESSION_KEY, JSON.stringify(value))
  }, [])

  const clearToken = useCallback(() => {
    // Clears the session if there is one, otherwise the shared token. Signing
    // out of a personal session must not also wipe the shop's token from a
    // tablet a colleague is about to pick up.
    if (readSession()) writeRaw(SESSION_KEY, null)
    else writeRaw(TOKEN_KEY, null)
  }, [])

  return useMemo(
    () => ({ ...resolveCredential(token, session), setToken, setSession, clearToken }),
    [session, token, setToken, setSession, clearToken],
  )
}
